import { Link } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

interface GiftPromoStripProps {
  title?: string | null;
  subtitle?: string | null;
  giftProduct?: { id: string; name: string; image_url: string | null; slug: string } | null;
  triggerProduct?: { id: string; name: string; slug: string } | null;
  ctaText?: string;
  ctaLink?: string;
}

export const GiftPromoStrip = ({
  title,
  subtitle,
  giftProduct,
  triggerProduct,
  ctaText = "Shop the offer",
  ctaLink = "/shop",
}: GiftPromoStripProps) => {
  return (
    <section className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-r from-primary/[0.06] via-primary/[0.03] to-accent/[0.05]"
        >
          <div className="absolute inset-y-0 -right-10 w-48 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute inset-y-0 -left-10 w-48 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 md:grid-cols-[auto,1fr,auto] items-center gap-5 p-5 sm:p-6">
            {/* Gift visual */}
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-card shadow-md border border-border flex items-center justify-center shrink-0 overflow-hidden">
                {giftProduct?.image_url ? (
                  <img
                    src={giftProduct.image_url}
                    alt={giftProduct.name}
                    className="w-full h-full object-contain p-2"
                    loading="lazy"
                  />
                ) : (
                  <Gift className="w-8 h-8 text-primary" />
                )}
                <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                  <Gift className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>

            {/* Copy */}
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase text-primary mb-1.5">
                <Gift className="w-3 h-3" /> Free gift
              </span>
              <h3 className="font-display text-base sm:text-xl font-bold text-foreground leading-snug">
                {title || "Free gift with selected phones"}
              </h3>
              {subtitle && (
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                  {subtitle}
                </p>
              )}
              {giftProduct && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Includes <span className="font-semibold text-foreground">{giftProduct.name}</span>
                  {triggerProduct && (
                    <> with every <span className="font-semibold text-foreground">{triggerProduct.name}</span></>
                  )}
                  .
                </p>
              )}
            </div>

            {/* CTA */}
            <Link
              to={triggerProduct ? `/product/${triggerProduct.slug}` : ctaLink}
              className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all"
            >
              {ctaText}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};